import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { ProjectStateController } from '../../services/project-state.js';
import { router } from '../../services/router.js';
import * as projectApi from '../../services/project-api.js';
import type { Project } from '../../types/project.js';

type ValidationState = 'idle' | 'pending' | 'valid' | 'invalid' | 'duplicate';

@customElement('cp-project-add-modal')
export class CpProjectAddModal extends LitElement {
  createRenderRoot() { return this; }

  private _projectState = new ProjectStateController(this);
  private _debounceTimer: ReturnType<typeof setTimeout> | null = null;

  @state() private _open = false;
  @state() private _pathValue = '';
  @state() private _nameValue = '';
  @state() private _validationState: ValidationState = 'idle';
  @state() private _validationError = '';
  @state() private _duplicateProject: Project | null = null;
  @state() private _recentProjects: Project[] = [];
  @state() private _submitting = false;
  @state() private _submitError = '';

  open(): void {
    this._resetForm();
    this._open = true;
    void this._loadRecentProjects();
  }

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this._onKeyDown);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onKeyDown);
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
  }

  private _close(): void {
    this._open = false;
  }

  private _resetForm(): void {
    this._pathValue = '';
    this._nameValue = '';
    this._validationState = 'idle';
    this._validationError = '';
    this._duplicateProject = null;
    this._submitting = false;
    this._submitError = '';
  }

  private async _loadRecentProjects(): Promise<void> {
    try {
      this._recentProjects = await projectApi.getRecentProjects();
    } catch {
      this._recentProjects = [];
    }
  }

  private _onPathInput(e: Event): void {
    this._pathValue = (e.target as HTMLInputElement).value;
    this._submitError = '';
    this._validateDebounced();
  }

  private _onNameInput(e: Event): void {
    this._nameValue = (e.target as HTMLInputElement).value;
  }

  private _validateDebounced(): void {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);

    const trimmed = this._pathValue.trim();
    if (!trimmed) {
      this._validationState = 'idle';
      this._validationError = '';
      this._duplicateProject = null;
      return;
    }

    this._validationState = 'pending';
    this._debounceTimer = setTimeout(() => void this._validate(), 500);
  }

  private async _validate(): Promise<void> {
    const trimmed = this._pathValue.trim();
    if (!trimmed) return;

    try {
      const result = await projectApi.validatePath(trimmed);
      if (this._pathValue.trim() !== trimmed) return;

      if (result.valid) {
        this._validationState = 'valid';
        this._validationError = '';
        this._duplicateProject = null;
        if (!this._nameValue) {
          const parts = (result.resolvedPath ?? trimmed).split('/');
          this._nameValue = parts[parts.length - 1] || '';
        }
      } else if (result.duplicate && result.existingProject) {
        this._validationState = 'duplicate';
        this._validationError = result.error ?? 'Projekt bereits registriert';
        this._duplicateProject = result.existingProject;
      } else {
        this._validationState = 'invalid';
        this._validationError = result.error ?? 'Ungueltiger Pfad';
        this._duplicateProject = null;
      }
    } catch {
      if (this._pathValue.trim() !== trimmed) return;
      this._validationState = 'invalid';
      this._validationError = 'Validierung fehlgeschlagen';
    }
  }

  private _onRecentClick(project: Project): void {
    this._pathValue = project.path;
    this._nameValue = project.name;
    this._validateDebounced();
  }

  private _onBackdropClick(e: Event): void {
    if ((e.target as HTMLElement).classList.contains('cp-project-add-modal__overlay')) {
      this._close();
    }
  }

  private _onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this._open) {
      this._close();
    }
  };

  private _onDuplicateLink(): void {
    if (this._duplicateProject) {
      this._close();
      this._projectState.setActiveProject(this._duplicateProject);
      router.navigateTo(`/projects/${this._duplicateProject.id}`);
    }
  }

  private async _onSubmit(): Promise<void> {
    if (this._validationState !== 'valid' || this._submitting) return;

    this._submitting = true;
    this._submitError = '';

    try {
      const name = this._nameValue.trim() || this._pathValue.trim().split('/').pop() || 'Projekt';
      const project = await projectApi.createProject({ name, path: this._pathValue.trim() });
      this._close();
      await this._projectState.refresh();
      this._projectState.setActiveProject(project);
      router.navigateTo(`/projects/${project.id}`);
    } catch (e) {
      this._submitError = e instanceof Error ? e.message : 'Projekt konnte nicht hinzugefuegt werden';
    } finally {
      this._submitting = false;
    }
  }

  private _renderValidationIcon() {
    switch (this._validationState) {
      case 'pending':
        return html`<span class="cp-project-add-modal__validation-icon cp-project-add-modal__validation-icon--pending">&#8635;</span>`;
      case 'valid':
        return html`<span class="cp-project-add-modal__validation-icon cp-project-add-modal__validation-icon--valid">&#10003;</span>`;
      case 'invalid':
        return html`<span class="cp-project-add-modal__validation-icon cp-project-add-modal__validation-icon--invalid">&#10007;</span>`;
      case 'duplicate':
        return html`<span class="cp-project-add-modal__validation-icon cp-project-add-modal__validation-icon--duplicate">&#9888;</span>`;
      default:
        return nothing;
    }
  }

  private _renderValidationMessage() {
    if (this._validationState === 'invalid') {
      return html`<p class="cp-project-add-modal__validation-msg cp-project-add-modal__validation-msg--error">${this._validationError}</p>`;
    }
    if (this._validationState === 'duplicate') {
      return html`
        <p class="cp-project-add-modal__validation-msg cp-project-add-modal__validation-msg--duplicate">
          ${this._validationError}
          ${this._duplicateProject ? html` &mdash; <a class="cp-project-add-modal__validation-link" @click=${this._onDuplicateLink}>Zum Projekt</a>` : nothing}
        </p>
      `;
    }
    return nothing;
  }

  render() {
    if (!this._open) return nothing;

    return html`
      <div class="cp-project-add-modal__overlay" @click=${this._onBackdropClick}>
        <div class="cp-project-add-modal__dialog">
          <div class="cp-project-add-modal__header">
            <h2 class="cp-project-add-modal__title">Projekt hinzufuegen</h2>
            <button class="cp-project-add-modal__close" @click=${this._close}>&times;</button>
          </div>

          <div class="cp-project-add-modal__body">
            <div class="cp-project-add-modal__field">
              <label class="cp-project-add-modal__label">Projekt-Pfad</label>
              <div class="cp-project-add-modal__input-wrap">
                <input
                  class="cp-project-add-modal__input cp-project-add-modal__input--path"
                  type="text"
                  placeholder="/pfad/zum/projekt"
                  .value=${this._pathValue}
                  @input=${this._onPathInput}
                />
                ${this._renderValidationIcon()}
              </div>
              ${this._renderValidationMessage()}
            </div>

            <div class="cp-project-add-modal__field">
              <label class="cp-project-add-modal__label">Projekt-Name</label>
              <input
                class="cp-project-add-modal__input"
                type="text"
                placeholder="Automatisch aus Pfad"
                .value=${this._nameValue}
                @input=${this._onNameInput}
              />
            </div>

            ${this._recentProjects.length > 0 ? html`
              <div class="cp-project-add-modal__recent">
                <h3 class="cp-project-add-modal__recent-title">Zuletzt geoeffnet</h3>
                <ul class="cp-project-add-modal__recent-list">
                  ${this._recentProjects.map(p => html`
                    <li class="cp-project-add-modal__recent-item" @click=${() => this._onRecentClick(p)}>
                      <span class="cp-project-add-modal__recent-name">${p.name}</span>
                      <span class="cp-project-add-modal__recent-path">${p.path}</span>
                    </li>
                  `)}
                </ul>
              </div>
            ` : nothing}

            ${this._submitError ? html`
              <div class="cp-project-add-modal__error">${this._submitError}</div>
            ` : nothing}
          </div>

          <div class="cp-project-add-modal__footer">
            <button class="cp-project-add-modal__btn cp-project-add-modal__btn--cancel" @click=${this._close}>Abbrechen</button>
            <button
              class="cp-project-add-modal__btn cp-project-add-modal__btn--submit"
              ?disabled=${this._validationState !== 'valid' || this._submitting}
              @click=${this._onSubmit}
            >${this._submitting ? 'Wird hinzugefuegt...' : 'Hinzufuegen'}</button>
          </div>
        </div>
      </div>
    `;
  }
}

// Auto-attach modal to DOM and listen for trigger events
let modalInstance: CpProjectAddModal | null = null;

function getModal(): CpProjectAddModal {
  if (!modalInstance || !modalInstance.isConnected) {
    modalInstance = document.createElement('cp-project-add-modal') as CpProjectAddModal;
    document.body.appendChild(modalInstance);
  }
  return modalInstance;
}

document.addEventListener('project-add-request', () => {
  getModal().open();
});

declare global {
  interface HTMLElementTagNameMap {
    'cp-project-add-modal': CpProjectAddModal;
  }
}
